import { create } from 'zustand';
import { getEventLog } from '../services/expeditionLog';
import { reducer, initialState, type LogState } from '../components/trailhead/expeditionLogReducer';
import { councilApiClient } from '../services/councilApiClient';
import type { CouncilFrame } from '../services/councilWsClient';
import type { LogFilter } from '../components/trailhead/expeditionLogReducer';
import type { EventLogEntry } from '../types/council';

interface ExpeditionLogStore {
  logState: LogState;
  open: boolean;
  loading: boolean;       // backfill (init) in flight
  reconnecting: boolean;  // onReconnect catch-up in flight (flap guard)
  inFlight: Set<string>;  // G3b — decision_ids with a resolve POST in flight
  init: (sessionId: string) => Promise<void>;
  onFrame: (frame: CouncilFrame) => void;
  onReconnect: (sessionId: string) => Promise<void>;
  resolve: (sessionId: string, decisionId: string, decision: 'approve' | 'dismiss') => Promise<void>;
  setFilter: (filter: LogFilter) => void;
  toggleSquad: (squadId: string) => void;
  setOpen: (open: boolean) => void;
}

// G3b — synthesize a pending governor_proposed row from a ledger decision so aged-out
// asks (no live frame in the page) stay actionable. NEGATIVE seq: never collides with a
// real council_event_log seq, and any real positive applied/dismissed frame out-ranks it
// in decisionStatuses (buttons flip correctly); sorts to the bottom. (BLOCKER 3)
function _synthPending(d: any): EventLogEntry {
  const knob = d.knob ?? 'a setting';
  return {
    seq: -d.seq, event_type: 'governor_proposed', timestamp: d.created_at,
    source_persona_id: null, trail_id: null, correlation_id: null, squad_id: null,
    cascade_depth: 0,
    target: { decision_id: d.id, knob: d.knob, from_value: d.from_value,
              to_value: d.to_value, status: 'pending',
              reason: d.reason, trigger_type: d.trigger_type },   // _to_dict's key is `id`
    summary: `⚖ Governor proposes tightening ${knob} ${d.from_value ?? '?'} → ${d.to_value ?? '?'} — approve?`,
  };
}

export const useExpeditionLog = create<ExpeditionLogStore>((set, get) => ({
  logState: initialState,
  open: false,
  loading: false,
  reconnecting: false,
  inFlight: new Set(),
  init: async (sessionId) => {
    set({ loading: true });
    try {
      const page = await getEventLog(sessionId, { limit: 100 });
      set({ logState: reducer(get().logState, { type: 'BACKFILL', entries: page.entries }),
            loading: false });
    } catch (e) {
      set({ loading: false });
    }
    // S2 — governor pending backfill is the actionability floor for aged-out asks;
    // its own try/catch so a governor-endpoint hiccup never blanks the event log.
    try {
      const { decisions } = await councilApiClient.getGovernorDecisions(sessionId, 'pending');
      const known = new Set(get().logState.entries
        .map(e => (e.target as any)?.decision_id).filter(Boolean));
      const synth = (decisions || []).filter((d: any) => !known.has(d.id)).map(_synthPending);
      if (synth.length) {
        set({ logState: reducer(get().logState, { type: 'MERGE', entries: synth }) });
      }
    } catch (e) { /* backfill floor — live frames still drive actionability */ }
  },
  onFrame: (frame) => {
    if (frame.event_type !== 'event_log_entry') return;
    set({ logState: reducer(get().logState, { type: 'LIVE', entry: frame.entry }) });
  },
  onReconnect: async (sessionId) => {
    // Guard 1: the initial mount fires init() AND a synthetic ws_connected back-to-back;
    // skip the redundant catch-up while the first backfill is still loading (init covers it).
    // Guard 2: collapse a reconnect-flap storm — one catch-up in flight at a time.
    if (get().loading || get().reconnecting) return;
    set({ reconnecting: true });
    try {
      const maxSeen = get().logState.entries[0]?.seq;
      const page = await getEventLog(sessionId, maxSeen != null ? { after_seq: maxSeen } : {});
      set({ logState: reducer(get().logState, { type: 'MERGE', entries: page.entries }) });
    } catch (e) {
      // network flap during ws_connected — drop; next reconnect/backfill recovers (live≡reload)
    } finally {
      set({ reconnecting: false });
    }
  },
  resolve: async (sessionId, decisionId, decision) => {
    // Optimistic disable; the live GOVERNOR_APPLIED/DISMISSED frame is the row's only
    // status writer (via onFrame). Clear inFlight in finally (S1): on error the row
    // re-enables for retry; on success the live frame removes its actionability.
    set((s) => ({ inFlight: new Set(s.inFlight).add(decisionId) }));
    try {
      await councilApiClient.resolveGovernorDecision(sessionId, decisionId, { decision });
    } catch (e) {
      /* 409/raced/network — the row re-enables; the operator retries or a frame resolves it */
    } finally {
      set((s) => {
        const next = new Set(s.inFlight); next.delete(decisionId); return { inFlight: next };
      });
    }
  },
  setFilter: (filter) => set({ logState: reducer(get().logState, { type: 'SET_FILTER', filter }) }),
  toggleSquad: (squadId) => set({ logState: reducer(get().logState, { type: 'TOGGLE_SQUAD', squadId }) }),
  setOpen: (open) => set((s) => ({
    open, logState: open ? reducer(s.logState, { type: 'MARK_READ' }) : s.logState })),
}));
